/**
 * Query and mutation hooks over the role routes (P15–P18).
 *
 * **`GET /roles` is UNPAGINATED** — a bare `{ data }` with no `meta`
 * (`roles.routes.ts`), because a tenant's role list is a dozen rows, not an
 * estate. So this is a plain `useQuery` over `apiRequest`, which unwraps the
 * envelope, and the screen renders no `Pagination`. Reaching for
 * `usePaginatedQuery` here would send `page`/`pageSize` parameters the route's
 * schema does not accept.
 *
 * Writes reuse `useGestureKey` from the users feature — same §05.6 contract:
 * one `Idempotency-Key` per gesture, retries of the same payload reuse it, an
 * edited payload is a new gesture, success clears it.
 */

import type { CreateRoleRequest, RoleDto, UpdateRoleRequest } from '@rd/shared';
import { useMutation, useQueryClient, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest } from '../../lib/apiClient.js';
import { USERS_KEY, useGestureKey } from '../users/useUsers.js';

export const ROLES_KEY = ['roles'] as const;

export function useRoles(): UseQueryResult<RoleDto[], unknown> {
  return useQuery<RoleDto[]>({
    queryKey: ROLES_KEY,
    queryFn: () => apiRequest<RoleDto[]>('/roles'),
  });
}

/**
 * Role writes invalidate the users list too: a renamed role is visible in the
 * users table's Roles column, and leaving the old name there until the next
 * hard refresh would make the two screens disagree about the same fact.
 */
function useInvalidateRoles(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ROLES_KEY });
    void queryClient.invalidateQueries({ queryKey: USERS_KEY });
  };
}

export function useCreateRole() {
  const invalidate = useInvalidateRoles();
  const gesture = useGestureKey();

  return useMutation<RoleDto, unknown, CreateRoleRequest>({
    mutationFn: (body) =>
      apiRequest<RoleDto>('/roles', {
        method: 'POST',
        body,
        idempotencyKey: gesture.keyFor(JSON.stringify(body)),
      }),
    onSuccess: () => {
      gesture.clear();
      invalidate();
    },
  });
}

export function useUpdateRole() {
  const invalidate = useInvalidateRoles();
  const gesture = useGestureKey();

  return useMutation<RoleDto, unknown, { roleKey: string; changes: UpdateRoleRequest }>({
    mutationFn: ({ roleKey, changes }) =>
      apiRequest<RoleDto>(`/roles/${roleKey}`, {
        method: 'PATCH',
        body: changes,
        idempotencyKey: gesture.keyFor(JSON.stringify({ roleKey, changes })),
      }),
    onSuccess: () => {
      gesture.clear();
      invalidate();
    },
  });
}

export function useDeleteRole() {
  const invalidate = useInvalidateRoles();
  const gesture = useGestureKey();

  return useMutation<{ deleted: boolean }, unknown, string>({
    mutationFn: (roleKey) =>
      apiRequest<{ deleted: boolean }>(`/roles/${roleKey}`, {
        method: 'DELETE',
        idempotencyKey: gesture.keyFor(roleKey),
      }),
    onSuccess: () => {
      gesture.clear();
      invalidate();
    },
  });
}
